import { LoadingState, PageContainer } from '@osteopro/ui';
import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useInactivityLogout } from '../../hooks/use-inactivity-logout.js';
import { useAuth } from '../../lib/auth-provider.js';

/**
 * Garde d'accès. L'URL demandée est conservée dans `next` pour y revenir après la connexion :
 * un lien profond vers un dossier patient ne doit pas se perdre au passage par `/login`.
 *
 * C'est aussi le seul point qui couvre toute la zone authentifiée : la déconnexion après une heure
 * d'inactivité est armée ici, et nulle part ailleurs.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [location, navigate] = useLocation();

  useInactivityLogout(isAuthenticated);

  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    const target = `${location}${globalThis.location.search}`;
    const next = target === '/' ? '' : `?next=${encodeURIComponent(target)}`;
    navigate(`/login${next}`, { replace: true });
  }, [isAuthenticated, isLoading, location, navigate]);

  if (isLoading) {
    return (
      <PageContainer data-testid="state-loading">
        <LoadingState type="card" count={2} />
      </PageContainer>
    );
  }
  if (!isAuthenticated) return null;
  return <>{children}</>;
}
